import React, {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {joiResolver} from "@hookform/resolvers/joi";
import {
    approveRequest,
    autoDebtLoadRequest,
    resetPaymentSchedule,
    changeStateError,
    fetchPaymentSchedule
} from "../../../../redux/actions/autoLombardActions";
import useIcons from "../../hooks/useIcons";
import {RangeSlider, StatusButton, Modal} from "../../components";
import {approvalModalValidationScheme} from "../../ValidationSchemes";

import "./modal.scss";
import {rangeSliders} from "../../constants/constants";
import {iconNames} from "../../constants/icon-names";
import PaymentSchedule
    from "../../../common/Contract/CalculationTabs/AnnuityCalculation/PaymentSchedule/PaymentSchedule";
import {Message} from "../../../utilites";

const ApprovalModal = ({
                           isOpen,
                           onClose,
                           requestDetails,
                           estimatedValue
                       }) => {
    const dispatch = useDispatch();
    const {getIcon} = useIcons();
    const sliders = rangeSliders(requestDetails?.car_request);
    const {paymentSchedule} = useSelector(({autolombard}) => autolombard.currentRequest);
    const {error} = useSelector(({autolombard}) => autolombard.currentRequest) || {};

    const form = useForm({
        mode: "onChange",
        defaultValues: {
            approvedAmount: sliders[0].initialValue?.toFixed(2),
            approvedTerm: sliders[1].initialValue?.toFixed(2),
            estimatedValue: sliders[2].initialValue
        },
        resolver: joiResolver(approvalModalValidationScheme),
    });

    const [sliderValues, setSliderValues] = useState(
        sliders.map((slider) => slider.initialValue || slider.min)
    );

    const [inputValues, setInputValues] = useState(
        sliders.map((slider) => slider.initialValue)
    );

    useEffect(() => {
        if (sliderValues?.[0] > estimatedValue) {
            dispatch(changeStateError('Сумма заявки не может быть больше оценочной стоимости'));
        } else {
            dispatch(changeStateError(''));
        }
    }, [sliderValues])

    useEffect(() => {
        if (isOpen) {
            getDebt();
        }
    }, [sliderValues[0], sliderValues[1]]);

    useEffect(() => {
        dispatch(resetPaymentSchedule());
    }, [])

    const initializeSliders = () => {
        const updatedSliders = rangeSliders(requestDetails.car_request);
        const initialValues = updatedSliders.map(
            (slider) => slider.initialValue || slider.min
        );

        setSliderValues(initialValues);
        setInputValues(initialValues);
        form.reset({
            approvedAmount: initialValues[0]?.toFixed(2) || 500,
            approvedTerm: initialValues[1]?.toFixed(2) || 1,
            estimatedValue: initialValues[2]
        });
    };

    const getPaymentSchedule = () => {
        const {approvedAmount, approvedTerm} = form?.getValues()
        const data = {
            id: requestDetails.car_request.id,
            desiredAmount: +approvedAmount,
            desiredTerm: +approvedTerm
        }
        dispatch(fetchPaymentSchedule(data))
    }

    const handleInputChange = (index, value, name) => {
        const newInputValues = [...inputValues];
        newInputValues[index] = value;
        setInputValues(newInputValues);

        const parsedValue = parseInt(value, 10) || sliders[index].min;
        const newValue = !isNaN(parsedValue)
            ? Math.min(Math.max(parsedValue, sliders[index].min), sliders[index].max)
            : sliders[index].min;

        setSliderValues((prev) => {
            const newValues = [...prev];
            newValues[index] = newValue;
            return newValues;
        });

        form.setValue(`${name}`, value, {shouldValidate: true});
    };

    const handleBlur = (name) => {
        if (name === "approvedAmount" || name === "approvedTerm") {
            getDebt();
        }
    };

    const handleSliderChange = (index, values, name) => {
        const newValue = values[0];
        setSliderValues((prev) => {
            const newValues = [...prev];
            newValues[index] = newValue;
            return newValues;
        });
        setInputValues((prev) => {
            const newInputValues = [...prev];
            newInputValues[index] = newValue.toString();
            return newInputValues;
        });
        form.setValue(`${name}`, newValue, {shouldValidate: true});
    };
    const getDebt = () => {
        const data = {
            jobType: requestDetails.client_info.jobType,
            jobIndustry: requestDetails.client_info.jobIndustry,
            avgMonthlyIncome: requestDetails.client_info.avgMonthlyIncome,
            noCreditHistory: requestDetails.credit_report_calculation.noCreditHistory,
            accessDeniedByClient: requestDetails.credit_report_calculation.accessDeniedByClient,
            creditAmountSum: requestDetails.credit_report_calculation.creditAmountSum,
            creditOverdueSum: requestDetails.credit_report_calculation.creditOverdueSum,
            suretySum: requestDetails.credit_report_calculation.totalSuretySum,
            desiredTerm: sliderValues[1],
            desiredAmount: sliderValues[0],
            isAutoOnline: true,
        }
        dispatch(autoDebtLoadRequest({
            data,
            oneClientId: requestDetails?.client_info.oneAppId,
            requestId: requestDetails.car_request.id}));
    }

    const onSubmit = (data) => {
        dispatch(
            approveRequest({requestId: requestDetails.car_request.id, data})
        );
        onClose();
    };

    useEffect(() => {
        return () => {
            initializeSliders()
            dispatch(resetPaymentSchedule());
        };
    }, []);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            {error && <Message type="error" text={error}/>}

            <div className="autolombard-modal-header ">
                <p className="title">Укажите сумму и срок заявки</p>
                <button
                    className="button-icontrigger modal-closetrigger"
                    onClick={onClose}
                >
                    <span className="icon">{getIcon(iconNames.CROSS)}</span>
                </button>
            </div>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="form-element gap-20">
                        {sliders.map(({label, name, min, max, isVisibleRange, readOnly}, index) => (
                            <div className="form-element fullwidth" key={index}>
                                <label className="auto-label">{label}</label>
                                <div className="form-element-data fullwidth">
                                    <input
                                        className="input modal-input"
                                        type="number"
                                        max={max}
                                        step={name !== "approvedTerm" ? "0.01" : "1"}
                                        value={inputValues[index]}
                                        autoComplete="off"
                                        onChange={(e) =>
                                            handleInputChange(index, e.target.value, name)
                                        }
                                        placeholder={min.toString()}
                                        readOnly={readOnly}
                                        onBlur={() => handleBlur(name)}
                                        {...form.register(name)}
                                    />
                                    {form.formState.errors[name] && (
                                        <span className={"form-message error"}>
                      {form.formState.errors[name].message}
                    </span>
                                    )}
                                </div>
                                {
                                    isVisibleRange && (
                                        <RangeSlider
                                            min={min}
                                            max={max}
                                            values={[sliderValues[index]]}
                                            setValues={(values) =>
                                                handleSliderChange(index, values, name)
                                            }
                                            onChange={(value) => handleSliderChange(index, [value])}
                                        />
                                    )
                                }
                            </div>
                        ))}
                        <StatusButton
                            label='Рассчитать график платежей'
                            status="primary"
                            type="button"
                            fullWidth
                            onClick={getPaymentSchedule}
                            className='autolombard-modal-buttonSpacibg'
                        />
                    </div>
                    <StatusButton
                        label="Одобрить"
                        status="primary"
                        type="submit"
                        fullWidth
                        disabled={!form.formState.isValid || !paymentSchedule || sliderValues?.[0] > estimatedValue || error}
                        className='autolombard-modal-buttonSpacibg'
                    />
                </form>
            </FormProvider>
            {paymentSchedule && <PaymentSchedule calculationResult={paymentSchedule}/>}
        </Modal>
    );
};

export default ApprovalModal;
